<template>
  <div :style="!$smallScreen ? 'margin-right: 200px' : '10px'">
    <div v-if="isInactiveLink" class="service-order-link-inactive-text">
      <h2>This link has been deactivated. Please contact the branch if you have any questions.</h2>
    </div>
    <div v-else-if="loading" class="text-center px-1">
      <b-spinner></b-spinner>
    </div>
    <div v-else>
      <b-card
        class="overflow-auto"
        :style="`min-height: calc(-125px + 95vh); max-height: calc(-125px + 95vh); ${
          $smallScreen ? 'padding-bottom: 120px;' : ''
        }`"
      >
        <div v-if="notFound">
          <p>Service Order not found.</p>
        </div>
        <div v-else>
          <!-- Service Order Summary -->
          <b-row no-gutters class="vams-summary">
            <div class="mr-4">
              <label class="col-head">Service Order ID:</label>
              <h4>{{ serviceOrder.serviceOrderId }}</h4>
            </div>
            <div class="mr-4">
              <label class="col-head">Fleet Unit ID:</label>
              <h4>{{ serviceOrder.fleetUnitId }}</h4>
            </div>
            <div class="mr-4">
              <label class="col-head">Customer:</label>
              <h4>{{ serviceOrder.customer.customerName }}</h4>
            </div>
            <div class="mr-4">
              <label class="col-head">Customer ID:</label>
              <h4>{{ serviceOrder.customerId }}</h4>
            </div>
            <div class="mr-4">
              <label class="col-head">VIN/Serial:</label>
              <h4>{{ serviceOrder.vin }}</h4>
            </div>
          </b-row>
          <b-row no-gutters class="vams-summary">
            <div class="mr-4">
              <label class="col-head" style="display: inline-block; min-width: 120px">PO #:</label>
              <span>{{ serviceOrder.purchaseOrder }}</span>
            </div>
          </b-row>
          <b-row no-gutters class="vams-summary">
            <div class="mr-4">
              <label class="col-head" style="display: inline-block; min-width: 120px">Unit Year:</label>
              <span class="ml-1">{{ unit.year }}</span>
            </div>
          </b-row>
          <b-row no-gutters class="vams-summary">
            <div class="mr-4">
              <label class="col-head" style="display: inline-block; min-width: 120px">Unit Make:</label>
              <span class="ml-1">{{ unit.make }}</span>
            </div>
          </b-row>
          <b-row no-gutters class="vams-summary">
            <div class="mr-4">
              <label class="col-head" style="display: inline-block; min-width: 120px">Unit Model:</label>
              <span class="ml-1">{{ unit.model }}</span>
            </div>
          </b-row>
          <hr />

          <!-- Job Details -->
          <div class="section-title mb-3">Job Details</div>
          <b-table
            :items="serviceOrderJobs"
            :fields="[
              { key: 'complaint', label: 'Complaint' },
              { key: 'subtotal', label: 'Subtotal' }
            ]"
            small
            class="border"
            thead-class="d-none"
            details-td-class="p-0"
          >
            <template #cell(complaint)="{ item, detailsShowing, toggleDetails }">
              <div style="cursor: pointer" @click="toggleDetails(item)">
                <font-awesome-icon :icon="detailsShowing ? 'minus' : 'plus'" class="mr-2 text-primary" />
                <span class="text-primary">{{ item.complaint }}</span>
              </div>
            </template>
            <template #cell(subtotal)="{ item }">
              {{ item.subtotal || 0 | currency }}
            </template>
            <template #row-details="{ item }">
              <div class="p-3 estimate-link-line-item estimate-link-line-item-details">
                <div class="mb-2">
                  <label class="col-head">Cause:</label>
                  <p>{{ item.cause }}</p>
                </div>
                <div class="mb-2">
                  <label class="col-head">Correction:</label>
                  <p>{{ item.correction }}</p>
                </div>
                <div v-if="labor(item) != 0" class="mb-2">
                  <b-button
                    class="p-0 mr-1 mt-neg4px"
                    variant="link"
                    size="xsm"
                    @click="toggleLineItemDetails(item, 'Labor')"
                  >
                    <font-awesome-icon v-if="!item.showDetailsLabor" icon="plus" class="text-primary" />
                    <font-awesome-icon v-if="item.showDetailsLabor" icon="minus" class="text-primary" />
                  </b-button>
                  <span class="estimate-link-line-item-subheader">
                    LABOR:
                    <span class="ml-2">{{ labor(item) | currency }}</span>
                  </span>
                  <div v-if="item.showDetailsLabor">
                    <div v-for="line in laborLineDetails(item)" :key="line.description" class="ml-4">
                      <span>
                        <em>{{ line.description }}</em>
                      </span>
                    </div>
                  </div>
                </div>

                <div v-if="parts(item) != 0" class="mb-2">
                  <b-button
                    class="p-0 mr-1 mt-neg4px"
                    variant="link"
                    size="xsm"
                    @click="toggleLineItemDetails(item, 'Parts')"
                  >
                    <font-awesome-icon v-if="!item.showDetailsParts" icon="plus" class="text-primary" />
                    <font-awesome-icon v-if="item.showDetailsParts" icon="minus" class="text-primary" />
                  </b-button>
                  <span class="estimate-link-line-item-subheader">
                    PARTS:
                    <span class="ml-2">{{ parts(item) | currency }}</span>
                  </span>
                  <div v-if="item.showDetailsParts">
                    <div v-for="line in partsLineDetails(item)" :key="line.description" class="ml-4">
                      <span>
                        <em>
                          {{ line.partNumber }} | {{ line.description }} | {{ line.price | currency }} | ({{
                            line.quantityShip
                          }})
                        </em>
                        <hr
                          v-if="$smallScreen"
                          style="border: 1px dashed; margin-top: -0.05rem; margin-bottom: -0.05rem"
                        />
                      </span>
                    </div>
                  </div>
                </div>

                <div v-if="misc(item) != 0" class="mb-2">
                  <b-button
                    class="p-0 mr-1 mt-neg4px"
                    variant="link"
                    size="xsm"
                    @click="toggleLineItemDetails(item, 'Misc')"
                  >
                    <font-awesome-icon v-if="!item.showDetailsMisc" icon="plus" class="text-primary" />
                    <font-awesome-icon v-if="item.showDetailsMisc" icon="minus" class="text-primary" />
                  </b-button>
                  <span class="estimate-link-line-item-subheader">
                    MISC:
                    <span class="ml-2">{{ misc(item) | currency }}</span>
                  </span>
                  <div v-if="item.showDetailsMisc">
                    <div v-for="line in miscLineDetails(item)" :key="line.description" class="ml-4">
                      <span class="mr-4">
                        <em>{{ line.description }} {{ line.price | currency }}</em>
                      </span>
                    </div>
                  </div>
                </div>

                <div v-if="sublet(item) != 0" class="mb-2">
                  <b-button
                    class="p-0 mr-1 mt-neg4px"
                    variant="link"
                    size="xsm"
                    @click="toggleLineItemDetails(item, 'Sublet')"
                  >
                    <font-awesome-icon v-if="!item.showDetailsSublet" icon="plus" class="text-primary" />
                    <font-awesome-icon v-if="item.showDetailsSublet" icon="minus" class="text-primary" />
                  </b-button>
                  <span class="estimate-link-line-item-subheader">
                    SUBLET:
                    <span class="ml-2">{{ sublet(item) | currency }}</span>
                  </span>
                  <div v-if="item.showDetailsSublet">
                    <div v-for="line in subletLineDetails(item)" :key="line.description" class="ml-4">
                      <span class="mr-4">
                        <em>{{ line.description }} {{ line.price | currency }}</em>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </b-table>

          <div class="pt-3" />

          <!-- Service Order Footer -->
          <b-row no-gutters class="mt-3 pt-2 border-top vams-summary">
            <div class="mr-4">
              <label class="col-head" style="display: inline-block; min-width: 120px">Subtotal:</label>
              <span>{{ serviceOrder.subtotal || 0 | currency }}</span>
            </div>
          </b-row>
          <b-row no-gutters class="vams-summary">
            <div class="mr-4">
              <label class="col-head" style="display: inline-block; min-width: 120px">Shop Supplies:</label>
              <span>{{ serviceOrder.shopSupplies || 0 | currency }}</span>
            </div>
          </b-row>
          <b-row no-gutters class="vams-summary">
            <div class="mr-4">
              <label class="col-head" style="display: inline-block; min-width: 120px">EPA Charge:</label>
              <span>{{ serviceOrder.epaCharge || 0 | currency }}</span>
            </div>
          </b-row>
          <b-row no-gutters class="vams-summary">
            <div class="mr-4">
              <label class="col-head" style="display: inline-block; min-width: 120px">Pre-Tax Total:</label>
              <span>
                {{
                  ((serviceOrder.subtotal || 0) + (serviceOrder.shopSupplies || 0) + (serviceOrder.epaCharge || 0))
                    | currency
                }}
              </span>
            </div>
          </b-row>
          <b-row no-gutters class="vams-summary">
            <div class="mr-4">
              <label class="col-head" style="display: inline-block; min-width: 120px">Sales Tax:</label>
              <span>{{ serviceOrder.salesTax || 0 | currency }}</span>
            </div>
          </b-row>
          <b-row no-gutters class="vams-summary">
            <div class="mr-4">
              <label class="col-head" style="display: inline-block; min-width: 120px">Total:</label>
              <span>{{ serviceOrder.total || 0 | currency }}</span>
            </div>
          </b-row>
          <div style="margin-top: 20px; text-align: left">
            <button class="btn btn-dark" @click="payNow">PAY NOW</button>
          </div>
          <div
            :style="`margin-top: 15px; font-size: 12px; color: #666; text-align: left; ${
              $smallScreen ? 'padding-bottom: 100px; margin-bottom: 16px;' : ''
            }`"
          >
            <p style="margin: 0">
              *This is a summary of your service order. A full detailed invoice is available upon request and will be
              provided with your receipt.
            </p>
          </div>
        </div>
      </b-card>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { GuidLinkActions, GuidLinkGetters } from '@/shared/store/guid-link/types';
import ErrorService from '@/shared/services/ErrorService';

export default {
  name: 'PayServiceOrderPage',
  data() {
    return {
      loading: false,
      notFound: false,
      isInactiveLink: false
    };
  },
  computed: {
    ...mapGetters({
      serviceOrder: GuidLinkGetters.GET_SERVICE_ORDER,
      unit: GuidLinkGetters.GET_UNIT,
      paymentLinkData: GuidLinkGetters.GET_PAYMENT_LINK
    }),
    serviceOrderJobs() {
      return Object.values(this.serviceOrder.jobs || {}).map(job => {
        const clonedJob = JSON.parse(JSON.stringify(job || {}));
        clonedJob.showDetailsLabor = false;
        clonedJob.showDetailsParts = false;
        clonedJob.showDetailsMisc = false;
        clonedJob.showDetailsSublet = false;
        return clonedJob;
      });
    }
  },
  async created() {
    this.loading = true;
    try {
      await this[GuidLinkActions.FETCH_SERVICE_ORDER](this.$route.params.guid);
      await this[GuidLinkActions.FETCH_UNIT](this.serviceOrder.unitId);
    } catch (error) {
      let errorMessage = 'Error fetching Service Order.';
      if (error.response && error.response.status == 415) {
        errorMessage = 'Service Order Link has been deactivated.';
        this.isInactiveLink = true;
      } else if (error.response && error.response.status == 404) {
        this.notFound = true;
      }
      ErrorService.createErrorToast(this, errorMessage);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    ...mapActions([GuidLinkActions.FETCH_SERVICE_ORDER, GuidLinkActions.FETCH_UNIT, GuidLinkActions.GET_PAYMENT_LINK]),
    labor(job) {
      let labor = 0;
      job.details?.laborLines?.forEach(lbr => (labor += lbr.price * lbr.hoursBill));
      return labor;
    },
    parts(job) {
      let partsPrice = 0;
      job.details?.parts?.forEach(pt => (partsPrice += pt.price * pt.quantityShip));
      return partsPrice;
    },
    misc(job) {
      let miscPrice = 0;
      job.details?.miscLines?.forEach(mis => (miscPrice += mis.price * mis.quantity));
      return miscPrice;
    },
    sublet(job) {
      let subletPrice = 0;
      job.details?.subletLines?.forEach(sub => (subletPrice += sub.price * sub.quantity));
      return subletPrice;
    },
    laborLineDetails(job) {
      let details = [];
      job.details.laborLines?.forEach(line => {
        const linePrice = line.price * line.hoursBill;
        if (linePrice !== 0) {
          details.push({ description: line.description });
        }
      });
      return details;
    },
    partsLineDetails(job) {
      let details = [];
      job.details.parts?.forEach(line => {
        const linePrice = line.price * line.quantityShip;
        if (linePrice !== 0) {
          var suffix = '';
          if (line.partNumber.endsWith('-CORE')) {
            suffix = '-CORE';
          }

          details.push({
            partNumber: line.partNumber,
            description: line.description + suffix,
            quantityShip: line.quantityShip,
            price: line.price
          });
        }
      });
      return details;
    },
    miscLineDetails(job) {
      let details = [];
      job.details.miscLines?.forEach(mis => {
        const linePrice = mis.price * mis.quantity;
        if (linePrice !== 0) {
          details.push({ description: mis.description, price: linePrice });
        }
      });
      return details;
    },
    subletLineDetails(job) {
      let details = [];
      job.details.subletLines?.forEach(sub => {
        const linePrice = sub.price * sub.quantity;
        if (linePrice !== 0) {
          details.push({ description: sub.description, price: linePrice });
        }
      });
      return details;
    },
    toggleLineItemDetails(job, section) {
      const propertyName = `showDetails${section}`;
      job[propertyName] = !job[propertyName];
    },
    async payNow() {
      try {
        await this[GuidLinkActions.GET_PAYMENT_LINK](this.$route.params.guid);
        const paymentLink = this.paymentLinkData;

        if (!paymentLink) {
          ErrorService.createErrorToast(this, 'Unable to get payment link.');
          return;
        }

        window.location.href = paymentLink;
      } catch (error) {
        ErrorService.createErrorToast(this, 'Error getting payment link.');
      }
    }
  }
};
</script>

<style scoped>
.service-order-link-inactive-text {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  color: #dc3545;
}

.service-order-link-inactive-text h2 {
  text-align: center;
}

>>> td {
  border-right: none !important;
}
</style>
